// import components
import ConfirmationDialog from '../ConfirmationDialog';
import DataGridHeader, {
  DataGridHeaderPortal
} from './components/DataGridHeader';
import GridContainer from './components/styled/GridContainer';
import GridStyleWrapper from './components/styled/GridStyleWrapper';
import NoRowsOverlay from './NoRowsOverlay';

// import hooks
import { useTranslation } from 'react-i18next';
import { useGetCustomDataGridColumnsToRender } from './hooks/useGetCustomDataGridColumnsToRender';

// import props
import type {
  DataGridProProps,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel
} from '@mui/x-data-grid-pro';

// import react hooks, events and props
import { useEffect, useState } from 'react';

// import utils
import { nlNL } from '@mui/x-data-grid/locales';

// import types
import { StyledDataGridPro } from './DataGrid.styles';
import { type CustomDataGridProps } from './DataGrid.types';

const CustomDataGrid = <T extends GridValidRowModel = any>({
  rows,
  columns,
  loading,
  title,
  getTreeDataPath,
  groupingColDef,
  onAddClick,
  onAddClickTitle,
  onDeleteClick,
  onEditClick,
  onCopyClick,
  onViewClick,
  customActions,
  actionsThreshold = 3,
  addRowDialog,
  additionalButtons,
  checkboxSelection,
  confirmationTitle,
  confirmationBody,
  customSelectionModel,
  disableActions,
  footer = null,
  initialState,
  pagination,
  page = 1,
  pageSize = 10,
  pageSizeOptions,
  onPaginationModelChange,
  onSelectionChange,
  slots,
  dynamicHeight = false,
  shouldRenderToolBar = false,
  headerContent,
  containerHeight,
  translationKey,
  canBeEditedProp,
  canBeDeletedProp,
  isRowSelectableFromParent,
  getExtraRowClassName,
  disableRowSelectionOnClick,
  tableBgColor = 'white',
  hasTreeData,
  apiRef,
  autosizeOnMount,
  autosizeOptions,
  rowHeight,
  ...rest
}: CustomDataGridProps<T>) => {
  'use memo';

  const { t, i18n } = useTranslation('common');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState<number | string | null>(
    null
  );

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set()
    });

  const [rowToDelete, setRowToDelete] = useState<T | null>(null);

  const [deleteSelection, setDeleteSelection] = useState(false);
  const [treeDataState, setTreeDataState] = useState<boolean>(false);

  const handleDeleteClick = (id: GridRowId, row?: T) => {
    setRowToDelete(row ?? null);
    setRowIdToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const onDeleteConfirmation = (isConfirmed: boolean) => {
    if (isConfirmed && onDeleteClick) {
      if (deleteSelection) {
        const rowsSelected = rows?.filter((r) =>
          rowSelectionModel.ids.has(r.id)
        );
        onDeleteClick(rowSelectionModel, rowsSelected);
        setDeleteSelection(false);
      } else if (rowIdToDelete != null && rowToDelete != null) {
        onDeleteClick([rowIdToDelete], [rowToDelete]);
        setRowIdToDelete(null);
      }
    }
    setShowDeleteConfirmation(false);
  };

  const onRowSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(selectionModel);
    onSelectionChange?.(
      selectionModel,
      rows?.filter((row) => selectionModel.ids.has(row.id))
    );
  };

  function noRows() {
    return <NoRowsOverlay translationKey={translationKey} />;
  }

  const columnsToRender = useGetCustomDataGridColumnsToRender(
    onViewClick,
    onCopyClick,
    onEditClick,
    onDeleteClick,
    handleDeleteClick,
    customActions,
    columns,
    actionsThreshold,
    disableActions,
    canBeEditedProp,
    canBeDeletedProp
  );

  const DataGridHeaderComponent = (
    <DataGridHeader
      onAddClickTitle={onAddClickTitle}
      additionalButtons={additionalButtons}
      onAddClick={onAddClick}
      onDeleteClick={onDeleteClick}
      title={title}
      rowSelectionModel={rowSelectionModel}
      setShowDeleteConfirmation={setShowDeleteConfirmation}
      setDeleteSelection={setDeleteSelection}
      disableActions={disableActions}
      checkboxSelection={checkboxSelection}
      headerContent={headerContent}
      {...(!shouldRenderToolBar && { sx: { mb: 3 } })}
    />
  );

  // merge isRowSelectable from parent and default rule
  const isRowSelectable = (params: GridRowParams) => {
    const defaultRule = !params.row.disableSelection;
    return isRowSelectableFromParent
      ? defaultRule && isRowSelectableFromParent(params)
      : defaultRule;
  };

  // merge getRowClassName from parent and default rule
  const mergedGetRowClassName = (
    params: GridRowParams & {
      indexRelativeToCurrentPage: number;
      isFirstVisible?: boolean;
      isLastVisible?: boolean;
    }
  ) => {
    // classes added from parent
    const external =
      typeof rest.getRowClassName === 'function'
        ? rest.getRowClassName({
            ...params,
            isFirstVisible: !!params.isFirstVisible,
            isLastVisible: !!params.isLastVisible
          })
        : '';

    const extra = getExtraRowClassName?.(params) ?? '';

    // default striped background
    const striped =
      params.indexRelativeToCurrentPage % 2 === 0
        ? 'stripedRow-even'
        : 'stripedRow-odd';

    const hideCheckbox = !isRowSelectable(params) ? 'dg-hide-checkbox' : '';

    return `${external} ${striped} ${extra} ${hideCheckbox}`.trim();
  };

  useEffect(() => {
    if (groupingColDef && !!getTreeDataPath && hasTreeData)
      setTreeDataState(true);
  }, [getTreeDataPath, groupingColDef, hasTreeData]);

  const adaptedTreeDataPath = getTreeDataPath
    ? (row: GridValidRowModel) => getTreeDataPath(row) as readonly string[]
    : undefined;

  return (
    <GridContainer style={{ height: containerHeight || 'inherit' }}>
      {!shouldRenderToolBar ? (
        DataGridHeaderComponent
      ) : (
        <DataGridHeaderPortal>{DataGridHeaderComponent}</DataGridHeaderPortal>
      )}
      <GridStyleWrapper height={dynamicHeight ? 'auto' : '100%'}>
        <StyledDataGridPro
          {...(rest as DataGridProProps<GridValidRowModel>)}
          apiRef={apiRef}
          disableRowSelectionOnClick={
            disableRowSelectionOnClick ?? !!addRowDialog
          }
          localeText={
            i18n.language.includes('nl')
              ? nlNL.components.MuiDataGrid.defaultProps.localeText
              : undefined
          }
          groupingColDef={groupingColDef}
          checkboxSelection={checkboxSelection && !disableActions}
          isRowSelectable={isRowSelectable}
          rows={rows}
          columns={columnsToRender}
          slots={{
            ...slots,
            noRowsOverlay: noRows
          }}
          slotProps={{
            loadingOverlay: {
              noRowsVariant: 'linear-progress'
            }
          }}
          rowSelectionModel={customSelectionModel || rowSelectionModel}
          onRowSelectionModelChange={(selectionModel) =>
            onRowSelectionModelChange(selectionModel)
          }
          loading={loading}
          getRowClassName={mergedGetRowClassName}
          treeData={treeDataState}
          getTreeDataPath={adaptedTreeDataPath}
          initialState={initialState}
          pagination={pagination}
          pageSizeOptions={pageSizeOptions}
          onPaginationModelChange={onPaginationModelChange}
          paginationModel={{ page, pageSize }}
          rowHeight={rows?.length === 0 ? 75 : rowHeight ?? 52} // If no rows set the height for the NoRowsOverlay
          tableBgColor={tableBgColor}
          autosizeOnMount={autosizeOnMount}
          autosizeOptions={autosizeOptions}
        />
        {showDeleteConfirmation && (
          <ConfirmationDialog
            handleAction={onDeleteConfirmation}
            cancelBtnText={t('no')}
            confirmBtnText={t('yes')}
            type="warning"
            title={confirmationTitle}
          >
            <p>{confirmationBody}</p>
          </ConfirmationDialog>
        )}
        {addRowDialog}
        {footer}
      </GridStyleWrapper>
    </GridContainer>
  );
};

export default CustomDataGrid;
